import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Notification } from '@senviro/core-api-client';
import { useAuth } from '@/auth/AuthProvider';
import { toast } from '@/components/ui/toast';
import { apiFetch } from '@/lib/api-fetch';
import {
  NOTIFICATIONS_QUERY_KEY,
  notificationsQueryKey,
} from './use-notifications';

/**
 * Realtime notification client (W6-F5-S2 + S3).
 *
 * Connects to the Senviro Socket.IO gateway at `/socket.io/`. On every
 * `notification.created` event we:
 *   • Invalidate the notifications query → bell + center re-fetch.
 *   • Surface a Sonner toast (debounced when ≥3 fire within 200ms).
 *
 * Connection lifecycle (S3):
 *   • `connect`        → status `connected`
 *   • `reconnect_attempt` → status `reconnecting`
 *   • `disconnect`     → status `reconnecting` (or `disconnected` after fail)
 *   • Exponential backoff via socket.io-client built-ins
 *     (1s → 2s → 4s → 8s → 16s; capped at 30s; max 10 attempts).
 *   • On reconnect we fetch any notifications since `lastSeenAt` (stored in
 *     localStorage) and re-toast new ones.
 *
 * The hook is a singleton across renders — multiple consumers share the
 * same socket. Stays mounted in the app shell.
 */
export type RealtimeStatus = 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

export interface RealtimeState {
  status: RealtimeStatus;
  unreadCount: number;
  lastEventAt: string | null;
}

interface SocketLike {
  on: (event: string, fn: (...args: unknown[]) => void) => void;
  off?: (event: string, fn?: (...args: unknown[]) => void) => void;
  io?: { on: (event: string, fn: (...args: unknown[]) => void) => void };
  connected: boolean;
  disconnect: () => void;
  connect: () => void;
}

type SocketFactory = (
  url: string,
  opts: { auth: { token: string }; reconnection: boolean; reconnectionAttempts: number; reconnectionDelay: number; reconnectionDelayMax: number; transports?: string[] },
) => SocketLike;

let socketFactoryOverride: SocketFactory | null = null;
/** Test-only escape hatch; production uses dynamic import of socket.io-client. */
export function __setSocketFactoryForTest(factory: SocketFactory | null): void {
  socketFactoryOverride = factory;
}

const LAST_SEEN_KEY = 'senviro.notifications.lastSeenAt';

export function useRealtimeNotifications(): RealtimeState {
  const { getAccessToken } = useAuth();
  const qc = useQueryClient();
  const [state, setState] = React.useState<RealtimeState>({
    status: 'connecting',
    unreadCount: 0,
    lastEventAt: null,
  });
  const toastBufferRef = React.useRef<Notification[]>([]);
  const toastTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    let socket: SocketLike | null = null;

    async function connect(): Promise<void> {
      const token = await getAccessToken();
      if (!token || cancelled) return;

      const factory = socketFactoryOverride ?? (await loadSocketFactory());
      if (!factory || cancelled) return;

      const url = readSocketUrl();
      socket = factory(url, {
        auth: { token },
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 1_000,
        reconnectionDelayMax: 30_000,
        transports: ['websocket', 'polling'],
      });

      socket.on('connect', () => {
        setState((prev) => ({ ...prev, status: 'connected' }));
        // On reconnect, fetch missed notifications so the bell stays accurate
        // even if the socket dropped during a delivery.
        const lastSeen = readLastSeen();
        if (lastSeen) {
          fetchMissed(lastSeen, getAccessToken)
            .then((items) => {
              if (items.length > 0) {
                toastBufferRef.current.push(...items);
                flushBuffered();
                void qc.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
              }
            })
            .catch(() => undefined);
        }
      });

      socket.on('disconnect', () => {
        setState((prev) => ({ ...prev, status: 'reconnecting' }));
      });

      const manager = socket.io;
      if (manager) {
        manager.on('reconnect_attempt', () => {
          setState((prev) => ({ ...prev, status: 'reconnecting' }));
        });
        manager.on('reconnect_failed', () => {
          setState((prev) => ({ ...prev, status: 'disconnected' }));
        });
      }

      socket.on('notification.created', (raw: unknown) => {
        const n = extractNotification(raw);
        if (!n) return;
        writeLastSeen(n.created_at);
        setState((prev) => ({
          status: 'connected',
          unreadCount: prev.unreadCount + (n.read_at == null ? 1 : 0),
          lastEventAt: n.created_at,
        }));
        toastBufferRef.current.push(n);
        flushBuffered();
        void qc.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
        void qc.invalidateQueries({ queryKey: notificationsQueryKey({ unreadOnly: true }) });
      });

      // Other event types (task.assigned, evidence.uploaded) just bust the
      // notifications cache so the bell + center pick them up in the next
      // poll. We intentionally do NOT toast for these — the surface is
      // notifications.created so taxonomy stays clean.
      const cacheBusters: string[] = [
        'task.assigned',
        'task.status_changed',
        'task.created',
        'evidence.uploaded',
        'evaluation.completed',
      ];
      for (const evt of cacheBusters) {
        socket.on(evt, () => {
          void qc.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
        });
      }
    }

    function flushBuffered(): void {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      toastTimerRef.current = setTimeout(() => {
        const buf = toastBufferRef.current;
        toastBufferRef.current = [];
        if (buf.length === 0) return;
        if (buf.length === 1) {
          toast.message(buf[0]!.title, {
            description: buf[0]!.body ?? undefined,
          });
        } else {
          toast.message(`${buf.length} new notifications`, {
            description: buf.slice(0, 3).map((n) => n.title).join(' • '),
          });
        }
      }, 200);
    }

    void connect();

    return () => {
      cancelled = true;
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      try {
        socket?.disconnect();
      } catch {
        // ignore
      }
    };
  }, [getAccessToken, qc]);

  return state;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function loadSocketFactory(): Promise<SocketFactory | null> {
  try {
    const mod = (await import('socket.io-client')) as unknown as {
      io: SocketFactory;
      default?: SocketFactory;
    };
    return mod.io ?? mod.default ?? null;
  } catch {
    return null;
  }
}

function readSocketUrl(): string {
  const raw = (
    (import.meta as ImportMeta).env as Record<string, string | undefined>
  ).VITE_SOCKET_URL;
  if (raw && raw.length > 0) return raw;
  // Default: same origin as the apps/api proxy.
  const apiBase = (
    (import.meta as ImportMeta).env as Record<string, string | undefined>
  ).VITE_API_BASE_URL;
  return apiBase ?? 'http://localhost:3001';
}

function readLastSeen(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(LAST_SEEN_KEY);
  } catch {
    return null;
  }
}

function writeLastSeen(iso: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LAST_SEEN_KEY, iso);
  } catch {
    // ignore
  }
}

async function fetchMissed(
  since: string,
  getAccessToken: () => Promise<string | null> | string | null,
): Promise<Notification[]> {
  try {
    const res = await apiFetch<{ items: Notification[] }>(
      `/api/v1/proxy/core/notifications?since=${encodeURIComponent(since)}`,
      // Background realtime-reconnect catch-up — already degrades to [] in
      // the catch below; suppress the global serverError toast too so a
      // notifications outage on reconnect doesn't blank the current page.
      { getAccessToken, skipServerErrorReport: true },
    );
    return Array.isArray(res?.items) ? res.items : [];
  } catch {
    return [];
  }
}

function extractNotification(raw: unknown): Notification | null {
  if (!raw || typeof raw !== 'object') return null;
  // Socket.IO event payload from the gateway is the full webhook body —
  // the notification itself lives under `payload` per Core ADR 0017. Fall
  // back to using the body as the notification when the shape is flat.
  const obj = raw as { payload?: unknown };
  const candidate = obj.payload ?? raw;
  if (typeof candidate !== 'object' || candidate === null) return null;
  const c = candidate as Partial<Notification>;
  if (!c.id || !c.created_at || !c.title) return null;
  return candidate as Notification;
}
